/**
 * HangarMax Import — types
 *
 * Two families live here:
 *  1. `Hm*` — the shapes HangarMax emits (admin export envelope, scene data,
 *     catalog manifest). Mirrors of the sibling project's runtime types; the
 *     Zod schemas in `parse.ts` are the enforcement point.
 *  2. `Planned*` / `ImportPlan` — what the engine produces for review before
 *     anything is written to PlaneMAX.
 *
 * Coordinate/unit note: HangarMax works in METERS with +X east, +Y up,
 * +Z south. PlaneMAX stores FEET with the same axis directions. All conversion
 * happens in `transform.ts` — nothing else in the engine multiplies by 0.3048.
 */

import type { DoorType } from '@/lib/templates/types'
import type { DoorsConfig } from '@/lib/templates/single-unit-community'
import type { HangarType } from '@/types/hangars'
import type { ObstacleShape, ObstacleType } from '@/types/obstacle'

// ============================================================================
// HangarMax source shapes
// ============================================================================

export interface HmAnchor {
  latitude: number
  longitude: number
  /** Camera-only. NOT part of the geometry — never fold this into a rotation. */
  bearing?: number | null
}

export type HmBuildingType = 'box' | 't_hangar'
export type HmDoorWall = 'front' | 'back' | 'left' | 'right'
export type HmDoorType = 'bifold' | 'sliding' | 'rolling' | 'hydraulic' | 'personnel'

export interface HmDoor {
  wall: HmDoorWall
  width_m: number
  height_m: number
  /** Signed offset from the wall CENTRE, metres. */
  offset_m: number
  type: HmDoorType
  open_direction?: string | null
}

export interface HmTHangarConfig {
  wing_width_m?: number | null
  wing_depth_m?: number | null
  tail_width_m?: number | null
  tail_depth_m?: number | null
}

export interface HmBuildingConfig {
  id: string
  name?: string | null
  type: HmBuildingType
  /** INTERIOR floor footprint, metres. Walls are added OUTSIDE this box. */
  width_m: number
  depth_m: number
  height_m: number
  /** Degrees clockwise. At 0 the front/door wall faces SOUTH. */
  bearing: number
  doors?: HmDoor[] | null
  /** Metres from the scene anchor: +X east, +Z south. */
  scene_position?: { x: number; z: number } | null
  wall_thickness_in?: number | null
  style?: string | null
  dimension_mode?: string | null
  t_hangar_config?: HmTHangarConfig | null
  wall_texture?: string | null
  floor_texture?: string | null
  door_texture?: string | null
  doors_open?: boolean | null
}

export type HmItemType =
  | 'aircraft'
  | 'vehicle'
  | 'equipment'
  | 'structural'
  | 'custom'
  | 'annotation'

export interface HmItemOverrides {
  width_m?: number | null
  depth_m?: number | null
  height_m?: number | null
  color?: string | null
  procedural_shape?: string | null
  annotation_type?: string | null
}

export interface HmItem {
  id: string
  type: HmItemType
  catalog_id?: string | null
  label?: string | null
  /**
   * Metres, ALWAYS scene-root relative (never building-local) even when
   * `parent_building` is set — that field is a soft grouping tag only.
   */
  position: { x: number; y: number; z: number }
  /** Degrees clockwise from north. */
  rotation?: number | null
  parent_building?: string | null
  overrides?: HmItemOverrides | null
}

/** Compact per-catalog-entry cache embedded in admin exports. */
export interface HmItemCacheEntry {
  /** 8-char model hash (HangarMax R2 key — NOT comparable to PlaneMAX hashes). */
  m?: string | null
  /** Width (wingspan for aircraft), length, height — metres. */
  w?: number | null
  l?: number | null
  h?: number | null
  /** 'p' = procedural. Absent means a GLTF model. */
  mt?: string | null
  /** Procedural shape / colour. */
  ps?: string | null
  pc?: string | null
}

export type HmItemCache = Record<string, HmItemCacheEntry>

export interface HmSceneData {
  version?: number | null
  anchor: HmAnchor
  buildings?: HmBuildingConfig[] | null
  items?: HmItem[] | null
  /** Everything below is preserved but not imported (see the gap list in the plan). */
  camera?: unknown
  mapCenter?: unknown
  mapZoom?: number | null
  mapBearing?: number | null
  mapPitch?: number | null
  mapStyle?: string | null
  groundTexture?: string | null
  airportCode?: string | null
  locationAddress?: string | null
  flythroughs?: unknown
}

export interface HmLayoutRow {
  id?: string | null
  name?: string | null
  scene_data: HmSceneData
  item_cache?: HmItemCache | null
  deleted_at?: string | null
}

export interface HmExportEnvelope {
  export_version?: number | null
  kind?: 'layout' | 'location' | null
  exported_at?: string | null
  exported_by?: string | null
  source?: { project_ref?: string | null } | null
  owner?: unknown
  location?: Record<string, unknown> | null
  layouts?: HmLayoutRow[] | null
  counts?: unknown
}

/** One entry of `catalog-manifest.json` (`items[]`), as served by the HangarMax CDN. */
export interface HmManifestItem {
  /** Catalog UUID — matches `HmItem.catalog_id`. */
  id: string
  /** Display name, e.g. "Cessna 402". */
  n?: string | null
  /** Category: aircraft | vehicle | equipment | structural | ... */
  c?: string | null
  /** Width (wingspan), length, height — metres. */
  w?: number | null
  l?: number | null
  h?: number | null
  /** Model hash8. */
  m?: string | null
  /** Model type: 'p' = procedural. */
  mt?: string | null
  /** Procedural shape / colour. */
  ps?: string | null
  pc?: string | null
  /** Manufacturer, when the manifest carries one. */
  mf?: string | null
}

// ============================================================================
// Catalog abstraction
// ============================================================================

/**
 * Normalised view of one HangarMax catalog entry, assembled from whichever
 * source is available: the embedded `item_cache` (admin bundles) or the CDN
 * catalog manifest (share links, and bundle enrichment when reachable).
 *
 * `item_cache` carries dimensions but no names, so name-based aircraft matching
 * only works when manifest entries are available.
 */
export interface HmCatalogEntry {
  catalogId: string
  name: string | null
  manufacturer: string | null
  category: string | null
  /** Metres. Width is wingspan for aircraft. */
  widthM: number | null
  lengthM: number | null
  heightM: number | null
  isProcedural: boolean
  proceduralShape: string | null
  proceduralColor: string | null
  modelHash: string | null
}

export interface CatalogSource {
  get(catalogId: string | null | undefined): HmCatalogEntry | undefined
  /** True when at least one entry carries a display name (enables name matching). */
  readonly hasNames: boolean
}

// ============================================================================
// Aircraft type matching
// ============================================================================

/**
 * Tiers, strongest first. `hash8` is deliberately absent: HangarMax and
 * PlaneMAX assign independent model hashes to the same airframe
 * (cessna-210 is 32cb5018 in HangarMax, 90fbcd20 in PlaneMAX), so the hash is
 * not a cross-system key despite the shared catalog ancestry.
 */
export type AircraftMatchMethod = 'type_code' | 'icao' | 'name' | 'dims' | null

export interface AircraftTypeCandidate {
  id: string
  typeCode: string
  icaoDesignator: string | null
  manufacturer: string | null
  model: string | null
  /** Feet. */
  wingspan: number | null
  length: number | null
  height: number | null
}

export interface AircraftMatch {
  method: AircraftMatchMethod
  aircraftTypeId: string | null
  /** 0..1 — 1 for exact key matches, lower for fuzzy/dimensional ones. */
  confidence: number
  typeLabel: string | null
}

// ============================================================================
// Import plan
// ============================================================================

export type LinkStatus = 'imported' | 'approximate' | 'unmatched' | 'source_deleted' | 'error'
export type PlannedOp = 'create' | 'update' | 'noop' | 'unmatched' | 'source_deleted'

/** Identifies the building an item sits in, before real DB ids exist. */
export interface PlannedHostRef {
  /** `HmBuildingConfig.id` of the host building. */
  buildingSourceId: string
  /** Index into that building's generated units. */
  unitIndex: number
}

export interface PlannedAreaWrite {
  name: string
  areaType: HangarType
  templateType: 'single_unit_community'
  latitude: number
  longitude: number
  /** Degrees, PlaneMAX scene convention (applied as +rotation.y). */
  rotationDeg: number
  widthFt: number
  depthFt: number
  heightFt: number
  doors: DoorsConfig
  floorColor: string
  wallColor: string
  /** Scene-space centre in feet relative to the PlaneMAX location centre. */
  sceneCenterFt: [number, number, number]
}

interface PlannedBase {
  op: PlannedOp
  sourceObjectId: string
  sourceHash: string
  /** Verbatim source object — persisted to `hangarmax_import_links.raw_source`. */
  raw: unknown
  /** Existing link row id, when this object was imported before. */
  linkId: string | null
  label: string
  fidelityNotes: string[]
}

export interface PlannedBuilding extends PlannedBase {
  kind: 'building'
  /** True when geometry was approximated (t-hangar → box). */
  approximate: boolean
  area: PlannedAreaWrite
  /** Existing targets from a previous run. */
  targetAreaId: string | null
  targetUnitIds: string[]
}

export interface PlannedAircraft extends PlannedBase {
  kind: 'aircraft'
  catalogId: string | null
  match: AircraftMatch
  /** Feet. */
  wingspanFt: number | null
  lengthFt: number | null
  heightFt: number | null
  manufacturer: string | null
  model: string | null
  /** Feet, scene-space relative to the location centre: [east, up, south]. */
  position: [number, number, number]
  /** Degrees, PlaneMAX scene convention. */
  rotationDeg: number
  host: PlannedHostRef | null
  targetAircraftId: string | null
  targetAllocationId: string | null
  /** Set on create; kept stable on update (tail numbers are user-owned). */
  tailNumber: string | null
}

export interface PlannedObstacle extends PlannedBase {
  kind: 'obstacle'
  catalogId: string | null
  host: PlannedHostRef
  name: string
  obstacleType: ObstacleType
  shape: ObstacleShape
  /** Unit-local feet. */
  positionX: number
  positionY: number
  /** Unit-local degrees (the renderer negates this). */
  rotation: number
  widthFt: number | null
  depthFt: number | null
  radiusFt: number | null
  heightFt: number
  color: string
  targetObstacleId: string | null
}

export interface PlannedSkip extends PlannedBase {
  kind: 'skip'
  op: 'unmatched'
  catalogId: string | null
  reason: string
}

export interface PlannedSourceDeleted {
  kind: 'source_deleted'
  op: 'source_deleted'
  linkId: string
  sourceObjectId: string
  sourceObjectType: 'building' | 'item'
  label: string
  targetTable: string | null
  targetId: string | null
}

export type PlannedAction =
  | PlannedBuilding
  | PlannedAircraft
  | PlannedObstacle
  | PlannedSkip
  | PlannedSourceDeleted

export interface ImportCounts {
  created: number
  updated: number
  unchanged: number
  unmatched: number
  sourceDeleted: number
}

export interface ImportSourceMeta {
  sourceKind: 'bundle_upload' | 'share_link'
  sourceLayoutId: string | null
  shareToken: string | null
  sourceProjectRef: string | null
  sourceExportVersion: number | null
  layoutName: string | null
}

export interface ImportPlan {
  meta: ImportSourceMeta
  sceneHash: string
  /** True when the incoming snapshot is byte-identical to the stored one. */
  sceneUnchanged: boolean
  anchor: HmAnchor
  buildings: PlannedBuilding[]
  aircraft: PlannedAircraft[]
  obstacles: PlannedObstacle[]
  skipped: PlannedSkip[]
  sourceDeleted: PlannedSourceDeleted[]
  warnings: string[]
  counts: ImportCounts
}

/** Existing link rows, as the planner needs them (subset of the DB row). */
export interface ExistingLink {
  id: string
  sourceObjectType: 'building' | 'item'
  sourceObjectId: string
  sourceHash: string
  status: LinkStatus
  targetTable: string | null
  targetId: string | null
  targetRefs: Record<string, unknown> | null
}

/** Everything the planner needs from the PlaneMAX side. */
export interface PlanContext {
  organizationId: string
  locationId: string
  /** PlaneMAX location centre — the scene origin. */
  locationCenter: { lat: number; lng: number }
  aircraftTypes: AircraftTypeCandidate[]
  existingLinks: ExistingLink[]
  /** Stored `scene_hash` of the source row, when re-syncing. */
  previousSceneHash: string | null
  /**
   * Ids of PlaneMAX rows that still exist (areas, aircraft, obstacles).
   * Links pointing outside this set are treated as "user deleted the target,
   * recreate it". Omit to assume every recorded target is still present.
   */
  existingTargetIds?: Set<string>
  /** Timestamp seed for TEMP tail numbers (injected so planning stays pure). */
  now: Date
}

export const HM_DOOR_TYPE_TO_PM: Record<HmDoorType, DoorType> = {
  bifold: 'bifold',
  sliding: 'sliding',
  rolling: 'rolling',
  hydraulic: 'hydraulic',
  // PlaneMAX has no personnel door. `manual` is the closest DB enum value but
  // the building-editor form only offers bifold/sliding/hydraulic/rolling, so
  // an imported `manual` door would fail validation on the first edit.
  personnel: 'rolling',
}
